import { useCallback } from "react";

import type {
    ActivationAlertsContractDisplayAdFeedGroup,
    ActivationAlertsContractSocialAdFeedGroup,
} from "@similarweb/pro-api-client";
import { useTracking } from "@similarweb/tracking-provider";
import { Flex } from "@similarweb/ui-components/foundations";

import { ModalShell } from "../../../common/modal/ModalShell";
import { useSelectedGroupItemStore } from "../../../common/modal/useSelectedGroupItemStore";

import { DisplayAdContent } from "./display/DisplayAdContent";
import { Header } from "./Header";
import { SocialAdContent } from "./social/SocialAdContent";

const ADDITIONAL_ADS_TRIGGER_INDEX = 7;

type AdsAlertModalInnerProps = {
    favicon: string;
    group: ActivationAlertsContractDisplayAdFeedGroup | ActivationAlertsContractSocialAdFeedGroup;
};

export const AdsAlertModalInner = ({ favicon, group }: AdsAlertModalInnerProps) => {
    const selectedItemIndex = useSelectedGroupItemStore((store) => store.selectedItemIndex);
    const setSelectedItemIndex = useSelectedGroupItemStore((store) => store.setSelectedItemIndex);
    const reset = useSelectedGroupItemStore((store) => store.reset);
    const { track } = useTracking();

    const domain = group.domain;
    const totalAdsCount = group.totalCount;
    const hasMoreAds = totalAdsCount > group.ads.length;

    const shouldTriggerFetch =
        selectedItemIndex != null &&
        selectedItemIndex >= ADDITIONAL_ADS_TRIGGER_INDEX &&
        selectedItemIndex < totalAdsCount;

    const handleClose = useCallback(() => {
        if (group.groupType === "Display") {
            track("Alert Card", "click", "close alert/alerts/display-ads");
        }
        if (group.groupType === "Social") {
            track("Alert Card", "click", "close alert/alerts/social-ads");
        }
        reset();
    }, [track, reset, group.groupType]);

    if (selectedItemIndex == null || selectedItemIndex >= totalAdsCount) {
        return null;
    }

    const adCounter = `${selectedItemIndex + 1}/${totalAdsCount}`;
    const shouldFetchMoreAds = hasMoreAds && shouldTriggerFetch;

    return (
        <ModalShell
            selectedIndex={selectedItemIndex}
            itemCount={totalAdsCount}
            onIndexChange={setSelectedItemIndex}
            onClose={handleClose}
            dataAutomation="ads-alert-modal"
            header={
                <Header
                    domain={domain}
                    favicon={favicon}
                    adCounter={adCounter}
                    onClose={handleClose}
                />
            }
        >
            <Flex
                gap="48px"
                padding="24px 16px 8px"
            >
                {group.groupType === "Display" ? (
                    <DisplayAdContent
                        group={group}
                        selectedItemIndex={selectedItemIndex}
                        shouldFetchMoreAds={shouldFetchMoreAds}
                    />
                ) : null}
                {group.groupType === "Social" ? (
                    <SocialAdContent
                        group={group}
                        selectedItemIndex={selectedItemIndex}
                        shouldFetchMoreAds={shouldFetchMoreAds}
                    />
                ) : null}
            </Flex>
        </ModalShell>
    );
};
